/**
 * AgentTeleportModal.tsx
 *
 * Operator-driven one-shot teleport for a registered AI agent.
 *
 * Surface:
 *   - Place typeahead (Mapbox + GeoLink cache, same plumbing as the
 *     `AgentMovementPanel` waypoint editor + the cinematic
 *     `AgentTeleportHUD`).
 *   - Country defaults to the agent's currently configured movement
 *     country (or the suggestion's inferred country if absent).
 *   - On confirm we mint / re-use the operator's movement session
 *     token, POST `/movement/teleport`, and let the local
 *     `agentFeedHub` broadcast push the new marker position to every
 *     connected browser without waiting for GeoLink's relay.
 *
 * This is intentionally distinct from `AgentMovementPanel`'s
 * waypoint loop: a teleport is a single hop, doesn't add to the
 * saved waypoints, and doesn't start the recurring tick. Operators
 * use it to "move my guide agent to this UAE attraction" without
 * setting up a tour first.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ensureMovementSession,
  getMovementStatus,
  pauseMovement,
  teleportAgent,
  type MovementStatusResponse,
} from '../services/agentMovementService';
import {
  suggestPlaces,
  type TeleportPlaceSuggestion,
} from '../services/teleportPlaceSearch';
import type { CachedAgentRecord } from '../services/agentService';
import type { SigningWallet, PasskeySigner } from '../services/profileService';
import { humanizeUnknownError } from '../utils/sorobanContractErrors';
import {
  getResidencySnapshot,
  type ResidencySnapshot,
} from '../services/citizenshipService';
import {
  runResidencyChangeChain,
  type ResidencyChainStep,
  type ResidencyChainStepStatus,
} from '../services/citizenshipPasskeyChain';

// ── Residency-change chain ─────────────────────────────────────────────────
//
// When the operator picks a destination country that doesn't match
// the agent's current active residency, the contract-side
// `_guardResidency` will reject the teleport with `wrong_country`.
// This chain orchestrates a full residency switch first:
//
//   1. relinquish (only if there's an active country) — agent signs
//      `relinquish(caller=agent, agent, country)`.
//   2. apply for the new country — agent signs `apply_for_country`.
//      Creates a PENDING row.
//   3. pay + confirm — operator passkey signs a USDC SAC transfer to
//      the X402 treasury; the backend tacks on the service-account
//      envelope signature, submits the transfer, then admin-invokes
//      the contract's `confirm`. This flips PENDING → ACTIVE.
//   4. poll snapshot until the new country is reflected (ledger
//      propagation can take a few seconds even after the confirm tx
//      is mined).
//   5. teleport — normal `/movement/teleport` call.
//
// For Case A (sameWalletAsAgent, the user's smart-account agent
// where agent === operator wallet), all three signatures use the
// same passkey. Case B (separate agent) is not supported by this
// chain — the agent's signing material isn't reachable from the
// browser. Mismatch UI explains the situation.

type ChainStep = ResidencyChainStep | 'teleport';

type ChainStepStatus = ResidencyChainStepStatus;

interface ChainStepState {
  status: ChainStepStatus;
  detail?: string;
}

interface ChainState {
  running:   boolean;
  failedAt?: ChainStep;
  error?:    string;
  steps:     Record<ChainStep, ChainStepState>;
}

const CHAIN_STEPS: ChainStep[] = ['relinquish', 'apply', 'confirm', 'poll', 'teleport'];

const CHAIN_STEP_LABELS: Record<ChainStep, string> = {
  relinquish: 'Relinquish old residency',
  apply:      'Apply for new residency',
  confirm:    'Pay residency fee & confirm',
  poll:       'Wait for chain to settle',
  teleport:   'Teleport to destination',
};

function idleChain(): ChainState {
  return {
    running: false,
    steps: CHAIN_STEPS.reduce((acc, s) => {
      acc[s] = { status: 'pending' };
      return acc;
    }, {} as Record<ChainStep, ChainStepState>),
  };
}

interface AgentTeleportModalProps {
  agent:          CachedAgentRecord;
  operatorWallet: string;
  stellarWallet:  SigningWallet | null;
  passkey:        PasskeySigner  | null;
  /** Hint used as the country scope for the place search and as the
   *  fallback country when the suggestion's own country is missing
   *  (e.g. POIs Mapbox doesn't tag). Optional — operator can edit. */
  defaultCountry: string | null;
  onClose:        () => void;
  onTeleported?:  () => void;
}

const AgentTeleportModal: React.FC<AgentTeleportModalProps> = ({
  agent,
  operatorWallet,
  stellarWallet,
  passkey,
  defaultCountry,
  onClose,
  onTeleported,
}) => {
  const [country, setCountry] = useState<string>((defaultCountry || '').toUpperCase());
  const [search, setSearch] = useState('');
  const [suggestions, setSuggestions] = useState<TeleportPlaceSuggestion[]>([]);
  const [picked, setPicked] = useState<TeleportPlaceSuggestion | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [loopStatus, setLoopStatus] = useState<MovementStatusResponse | null>(null);
  const [pauseAfter, setPauseAfter] = useState(true);
  // Citizenship snapshot — the position-write guard rejects when the
  // agent's active residency country doesn't match the target country
  // (see server/services/agentPositionService.js#_guardResidency). We
  // fetch this on open so the operator sees the mismatch before they
  // commit a teleport that's going to be rejected.
  const [residency, setResidency] = useState<ResidencySnapshot | null>(null);
  const [residencyLoading, setResidencyLoading] = useState(true);
  const [residencyError, setResidencyError] = useState<string | null>(null);
  // Residency-change chain state. The four steps map 1:1 onto the
  // primitives in citizenshipService.ts; we run them sequentially
  // with passkey signatures between each, then poll the snapshot
  // until propagation catches up, then teleport.
  const [chain, setChain] = useState<ChainState>(idleChain());
  const searchAbortRef = useRef<AbortController | null>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Snapshot of the running loop so we can show the operator what
  // they're about to interrupt. Single fetch on open — the modal
  // typically lives for a few seconds before commit / dismiss, so
  // there's no need to poll.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await getMovementStatus(agent.address);
        if (!cancelled) setLoopStatus(s);
      } catch { /* swallow */ }
    })();
    return () => { cancelled = true; };
  }, [agent.address]);

  useEffect(() => {
    let cancelled = false;
    setResidencyLoading(true);
    setResidencyError(null);
    (async () => {
      try {
        const snap = await getResidencySnapshot(agent.address);
        if (!cancelled) setResidency(snap);
      } catch (e) {
        if (!cancelled) setResidencyError(humanizeUnknownError(e));
      } finally {
        if (!cancelled) setResidencyLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [agent.address]);

  const loopRunning = loopStatus?.status === 'running' || loopStatus?.status === 'paused';
  const loopRandomCold = !!loopStatus
    && (loopStatus.mode === 'random' || loopStatus.mode === 'both')
    && !!loopStatus.randomDiagnostics
    && loopStatus.randomDiagnostics.slugAttempts >= 6
    && loopStatus.randomDiagnostics.slugHits === 0
    && loopStatus.randomDiagnostics.nearbyHits === 0;

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, []);

  // Esc-to-close so operators can dismiss the modal without grabbing
  // the mouse — matches the AgentDisposeModal pattern.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (searchAbortRef.current)    searchAbortRef.current.abort();
    const q = search.trim();
    if (q.length < 2) { setSuggestions([]); return; }
    searchDebounceRef.current = setTimeout(async () => {
      const ac = new AbortController();
      searchAbortRef.current = ac;
      const list = await suggestPlaces(q, {
        country: country || null,
        signal:  ac.signal,
      });
      if (searchAbortRef.current === ac) setSuggestions(list);
    }, 200);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [search, country]);

  const resolvedCountry = useMemo(() => {
    const c = (country || picked?.country || '').toUpperCase();
    return /^[A-Z]{2}$/.test(c) ? c : '';
  }, [country, picked]);

  // Residency mismatch detection. When the citizenship contract is
  // not configured on this env (`residency.configured === false`) the
  // backend guard short-circuits with `skipped: true` so any country
  // is fine. When configured and the agent has an active residency
  // somewhere, only that country's position writes succeed.
  const residencyCountry = residency?.country?.toUpperCase() || null;
  const residencyConfigured = residency?.configured === true;
  const residencyMismatch =
    residencyConfigured
    && !!residencyCountry
    && !!resolvedCountry
    && residencyCountry !== resolvedCountry;
  const residencyMissing =
    residencyConfigured
    && residency !== null
    && residency.country === null;

  const canTeleport = !!picked && !!resolvedCountry && !busy;

  // Chain orchestration: switch residency to the picked destination,
  // then teleport. Each step updates the visible chain state so the
  // operator sees what's happening. Errors freeze the chain at the
  // failed step; the operator can dismiss the modal and try again.
  const handleChangeResidencyAndTeleport = useCallback(async () => {
    if (!picked || !resolvedCountry) return;
    if (!passkey) {
      setChain((c) => ({ ...c, error: 'A passkey wallet is required to change residency.' }));
      return;
    }

    setErr(null);

    const baseSteps: Record<ChainStep, ChainStepState> = {
      relinquish: { status: residencyCountry ? 'in_progress' : 'skipped' },
      apply:      { status: 'pending' },
      confirm:    { status: 'pending' },
      poll:       { status: 'pending' },
      teleport:   { status: 'pending' },
    };
    setChain({ running: true, steps: baseSteps });

    const advance = (step: ChainStep, status: ChainStepStatus, detail?: string) =>
      setChain((c) => ({
        ...c,
        steps: { ...c.steps, [step]: { status, detail } },
      }));

    const fail = (step: ChainStep, e: unknown) => {
      const msg = humanizeUnknownError(e);
      setChain((c) => ({
        ...c,
        running:  false,
        failedAt: step,
        error:    msg,
        steps:    { ...c.steps, [step]: { status: 'failed', detail: msg } },
      }));
    };

    try {
      await runResidencyChangeChain({
        agentAddress:    agent.address,
        passkey,
        targetCountry:   resolvedCountry,
        currentCountry:  residencyCountry,
        onStep: (step, status, detail) => advance(step, status, detail),
      });
      const snap = await getResidencySnapshot(agent.address).catch(() => null);
      if (snap) setResidency(snap);

      advance('teleport', 'in_progress');
      try {
        await ensureMovementSession({
          operator:      operatorWallet,
          agent:         agent.address,
          stellarWallet,
          passkey,
        });
        await teleportAgent(
          operatorWallet,
          agent.address,
          {
            lat:     picked.lat,
            lng:     picked.lng,
            country: resolvedCountry,
            name:    picked.placeName,
          },
          { stellarWallet, passkey },
        );
        if (pauseAfter && loopStatus?.status === 'running') {
          try {
            await pauseMovement(operatorWallet, agent.address, { stellarWallet, passkey });
          } catch (pauseErr) {
          }
        }
        advance('teleport', 'done');
        setChain((c) => ({ ...c, running: false }));
        onTeleported?.();
        setTimeout(() => onClose(), 600);
      } catch (e) {
        fail('teleport', e);
      }
    } catch (e) {
      const msg = humanizeUnknownError(e);
      setChain((c) => ({
        ...c,
        running:  false,
        error:    msg,
        failedAt: CHAIN_STEPS.find((s) => c.steps[s].status === 'failed'),
      }));
    } finally {
      setChain((c) => ({ ...c, running: false }));
    }
  }, [
    picked,
    resolvedCountry,
    passkey,
    residencyCountry,
    agent.address,
    operatorWallet,
    stellarWallet,
    pauseAfter,
    loopStatus,
    onTeleported,
    onClose,
  ]);

  const handleTeleport = useCallback(async () => {
    if (!picked || !resolvedCountry) return;
    setErr(null);
    setBusy(true);
    try {
      await ensureMovementSession({
        operator:      operatorWallet,
        agent:         agent.address,
        stellarWallet,
        passkey,
      });
      await teleportAgent(
        operatorWallet,
        agent.address,
        {
          lat:     picked.lat,
          lng:     picked.lng,
          country: resolvedCountry,
          name:    picked.placeName,
        },
        { stellarWallet, passkey },
      );
      // Pause the recurring loop so the next random/predefined tick
      // doesn't immediately overwrite the just-teleported position.
      // Operators who want the agent to keep wandering after the demo
      // can uncheck the box.
      if (pauseAfter && loopStatus?.status === 'running') {
        try {
          await pauseMovement(operatorWallet, agent.address, { stellarWallet, passkey });
        } catch (pauseErr) {
          // Pause failure is non-fatal — the teleport already
          // succeeded; surface the message but keep the modal in
          // its success state.
        }
      }
      onTeleported?.();
      onClose();
    } catch (e: unknown) {
      setErr(humanizeUnknownError(e));
    } finally {
      setBusy(false);
    }
  }, [picked, resolvedCountry, operatorWallet, agent.address, stellarWallet, passkey, onTeleported, onClose, pauseAfter, loopStatus]);

  return (
    <div style={overlayStyle} onClick={onClose} role="dialog" aria-modal="true">
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <header style={headerStyle}>
          <div style={{ minWidth: 0 }}>
            <div style={titleStyle}>✈ Teleport agent</div>
            <div style={subTitleStyle} title={agent.address}>
              {agent.kindLabel && agent.kindLabel !== 'generic'
                ? `${agent.kindLabel} · `
                : ''}
              <code style={monoStyle}>{shortAddr(agent.address)}</code>
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" style={closeBtnStyle}>×</button>
        </header>

        <p style={hintStyle}>
          Move this agent to a single place. Doesn't start the recurring
          movement loop and doesn't change its saved waypoints. Useful for
          "show off this attraction" demos with a guide-type agent.
        </p>

        {/* Residency snapshot.
            Shown as soon as we know it because the backend's position-
            write guard rejects mismatched countries with `wrong_country`
            once the citizenship contract is configured (the teleport
            would silently fail otherwise). */}
        {residencyLoading && (
          <div style={residencyLoadingStyle}>Checking agent residency…</div>
        )}
        {!residencyLoading && residency && residencyConfigured && residencyCountry && (
          <div style={residencyMismatch ? residencyMismatchStyle : residencyOkStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: residencyMismatch ? 6 : 0 }}>
              <span style={residencyPillStyle(residencyMismatch ? 'mismatch' : 'ok')}>
                {residencyMismatch ? 'mismatch' : 'matches'}
              </span>
              <span style={{ fontSize: 12, color: '#334155' }}>
                Agent resides in <code style={inlineMonoStyle}>{residencyCountry}</code>
                {resolvedCountry ? <> · destination <code style={inlineMonoStyle}>{resolvedCountry}</code></> : null}
              </span>
            </div>
            {residencyMismatch && (
              <>
                <div style={{ fontSize: 11, color: '#991b1b', lineHeight: 1.45, marginBottom: 8 }}>
                  The position write will be rejected with <code style={inlineMonoStyle}>wrong_country</code>{' '}
                  because <code style={inlineMonoStyle}>{residencyCountry}</code> ≠{' '}
                  <code style={inlineMonoStyle}>{resolvedCountry}</code>. Change the agent's
                  residency to <code style={inlineMonoStyle}>{resolvedCountry}</code> in one click
                  (3 signatures + 1 USDC), or pick a place in{' '}
                  <code style={inlineMonoStyle}>{residencyCountry}</code>.
                </div>
                {!chain.running && !chain.failedAt && (
                  <button
                    type="button"
                    onClick={() => void handleChangeResidencyAndTeleport()}
                    disabled={!picked || !resolvedCountry}
                    style={changeBtnStyle}
                    title={
                      !picked
                        ? 'Search and pick a place first.'
                        : `Switch agent residency from ${residencyCountry} to ${resolvedCountry}, pay 1 USDC, then teleport.`
                    }
                  >
                    ✦ Change residency to {resolvedCountry} & teleport
                  </button>
                )}
                {(chain.running || chain.failedAt || CHAIN_STEPS.some((s) => chain.steps[s].status !== 'pending')) && (
                  <div style={chainBoxStyle}>
                    {CHAIN_STEPS.map((s) => {
                      const st = chain.steps[s];
                      return (
                        <div key={s} style={chainRowStyle}>
                          <span style={chainStatusDotStyle(st.status)}>
                            {chainStatusGlyph(st.status)}
                          </span>
                          <span style={chainLabelStyle(st.status)}>
                            {CHAIN_STEP_LABELS[s]}
                          </span>
                          {st.detail && (
                            <span style={chainDetailStyle}>{st.detail}</span>
                          )}
                        </div>
                      );
                    })}
                    {chain.error && (
                      <div style={chainErrorStyle}>
                        Chain stopped at <strong>{chain.failedAt}</strong>: {chain.error}
                      </div>
                    )}
                  </div>
                )}
                {chain.error && !chain.running && (
                  <button
                    type="button"
                    onClick={() => setChain(idleChain())}
                    style={chainRetryBtnStyle}
                  >
                    Reset & retry
                  </button>
                )}
              </>
            )}
          </div>
        )}
        {!residencyLoading && residency && residencyMissing && (
          <div style={residencyNeutralStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={residencyPillStyle('none')}>no residency</span>
              <span style={{ fontSize: 12, color: '#334155' }}>
                Agent has no active residency. Permissive deploys allow the teleport;
                strict deploys (<code style={inlineMonoStyle}>GEOTRUST_CITIZENSHIP_STRICT=true</code>)
                will reject with <code style={inlineMonoStyle}>no_active_residency</code>.
              </span>
            </div>
          </div>
        )}
        {!residencyLoading && residency && !residencyConfigured && (
          <div style={residencyNeutralStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={residencyPillStyle('skip')}>citizenship off</span>
              <span style={{ fontSize: 12, color: '#334155' }}>
                Citizenship contract is not configured on this env — residency
                guard is skipped.
              </span>
            </div>
          </div>
        )}
        {!residencyLoading && residencyError && (
          <div style={residencyNeutralStyle}>
            <div style={{ fontSize: 12, color: '#6b7280' }}>
              Couldn't load residency: {residencyError}
            </div>
          </div>
        )}

        <label style={fieldStyle}>
          <span style={fieldLabelStyle}>Country (ISO-2)</span>
          <input
            type="text"
            value={country}
            onChange={(e) => setCountry(e.target.value.toUpperCase().slice(0, 2))}
            placeholder="AE"
            maxLength={2}
            style={{ ...inputStyle, fontFamily: 'monospace', width: 80 }}
          />
        </label>

        <label style={fieldStyle}>
          <span style={fieldLabelStyle}>Place</span>
          <div style={{ position: 'relative' }}>
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPicked(null); }}
              placeholder={
                country
                  ? `Search a place in ${country}…`
                  : 'Search any place — set the country above for cache scoping'
              }
              style={{ ...inputStyle, width: '100%' }}
              spellCheck={false}
            />
            {suggestions.length > 0 && !picked && (
              <ul role="listbox" style={suggestionsListStyle}>
                {suggestions.map((s) => (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setPicked(s);
                        setSearch(s.placeName);
                        setSuggestions([]);
                        if (!country && s.country) setCountry(s.country);
                      }}
                      style={suggestionRowStyle}
                    >
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{s.placeName}</div>
                      <div style={{ fontSize: 11, color: '#6b7280' }}>
                        {s.lat.toFixed(4)}, {s.lng.toFixed(4)}
                        {s.country ? ` · ${s.country}` : ''}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </label>

        {picked && (
          <div style={previewStyle}>
            <div style={{ fontWeight: 700, fontSize: 13 }}>{picked.placeName}</div>
            <div style={{ fontSize: 11, color: '#6b7280', fontFamily: 'monospace' }}>
              {picked.lat.toFixed(5)}, {picked.lng.toFixed(5)}
              {(picked.country || resolvedCountry) ? ` · ${picked.country || resolvedCountry}` : ''}
            </div>
          </div>
        )}

        {loopRunning && (
          <div style={loopBoxStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={loopPillStyle(loopStatus!.status)}>
                {loopStatus!.status}
              </span>
              <span style={{ fontSize: 12, color: '#334155' }}>
                Movement loop is {loopStatus!.status} in
                {' '}<strong>{loopStatus!.mode}</strong> mode
                {loopStatus!.country ? <> · <code style={inlineMonoStyle}>{loopStatus!.country}</code></> : null}
              </span>
            </div>
            {loopRandomCold && (
              <div style={{ fontSize: 11, color: '#92400e', marginBottom: 6, lineHeight: 1.4 }}>
                GeoLink's cache is cold for {loopStatus!.country} — the random picker
                will keep erroring until something gets warmed. This teleport will
                seed the destination into the cache automatically.
              </div>
            )}
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#374151', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={pauseAfter}
                onChange={(e) => setPauseAfter(e.target.checked)}
                disabled={loopStatus!.status !== 'running'}
              />
              Pause the movement loop after teleporting
              {loopStatus!.status !== 'running' ? ' (already paused)' : ''}
            </label>
          </div>
        )}

        {err && (
          <div style={errorBoxStyle}>{err}</div>
        )}

        <div style={footerStyle}>
          <button onClick={onClose} style={cancelBtnStyle} disabled={busy}>Cancel</button>
          <button
            type="button"
            onClick={() => void handleTeleport()}
            disabled={!canTeleport}
            style={
              !canTeleport
                ? confirmBtnDisabledStyle
                : residencyMismatch
                  ? confirmBtnWarnStyle
                  : confirmBtnStyle
            }
            title={
              !picked ? 'Search and pick a place first.'
              : !resolvedCountry ? 'Set a 2-letter country code for the destination.'
              : residencyMismatch ? 'Will likely be rejected — agent residency country doesn\'t match destination.'
              : 'Send the agent to this place.'
            }
          >
            {busy
              ? 'Teleporting…'
              : residencyMismatch
                ? 'Teleport anyway'
                : 'Teleport now'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AgentTeleportModal;

function shortAddr(a: string): string {
  if (!a) return '';
  return a.length > 14 ? `${a.slice(0, 6)}…${a.slice(-6)}` : a;
}

// ── Styles ─────────────────────────────────────────────────────────────────

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9050,
};

const modalStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 12,
  padding: 18,
  maxWidth: 520,
  width: '92%',
  maxHeight: '88vh',
  overflow: 'auto',
  boxShadow: '0 24px 60px rgba(15, 23, 42, 0.35)',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: 12,
  marginBottom: 8,
};

const titleStyle: React.CSSProperties = {
  fontSize: 18,
  fontWeight: 700,
  color: '#111827',
};

const subTitleStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#6b7280',
  marginTop: 4,
};

const monoStyle: React.CSSProperties = {
  fontFamily: 'monospace',
  fontSize: 12,
  background: '#f3f4f6',
  padding: '1px 5px',
  borderRadius: 4,
};

const closeBtnStyle: React.CSSProperties = {
  border: 'none',
  background: 'transparent',
  fontSize: 22,
  lineHeight: 1,
  cursor: 'pointer',
  color: '#6b7280',
};

const hintStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#6b7280',
  margin: '6px 0 14px',
  lineHeight: 1.5,
};

const fieldStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: 12,
};

const fieldLabelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  color: '#6b7280',
  marginBottom: 4,
};

const inputStyle: React.CSSProperties = {
  boxSizing: 'border-box',
  padding: '8px 10px',
  borderRadius: 6,
  border: '1px solid #d1d5db',
  fontSize: 13,
};

const suggestionsListStyle: React.CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  margin: '4px 0 0',
  padding: 0,
  listStyle: 'none',
  background: '#fff',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
  zIndex: 5,
  maxHeight: 240,
  overflowY: 'auto',
};

const suggestionRowStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  border: 'none',
  background: 'transparent',
  padding: '8px 10px',
  cursor: 'pointer',
  borderBottom: '1px solid #f3f4f6',
};

const previewStyle: React.CSSProperties = {
  marginTop: 4,
  marginBottom: 14,
  padding: '10px 12px',
  borderRadius: 8,
  border: '1px solid #e5e7eb',
  background: '#f9fafb',
};

const errorBoxStyle: React.CSSProperties = {
  padding: '8px 12px',
  background: '#fef2f2',
  color: '#991b1b',
  borderRadius: 8,
  fontSize: 13,
  marginBottom: 10,
};

const loopBoxStyle: React.CSSProperties = {
  padding: '10px 12px',
  background: '#f8fafc',
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  marginBottom: 10,
};

const inlineMonoStyle: React.CSSProperties = {
  fontFamily: 'monospace',
  fontSize: 11,
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 4,
  padding: '0 4px',
};

function loopPillStyle(status: 'running' | 'paused' | 'stopped'): React.CSSProperties {
  const m: Record<string, React.CSSProperties> = {
    running: { background: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0' },
    paused:  { background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' },
    stopped: { background: '#e5e7eb', color: '#374151', border: '1px solid #d1d5db' },
  };
  return {
    ...m[status],
    padding: '1px 8px',
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  };
}

const footerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 8,
  marginTop: 12,
};

const cancelBtnStyle: React.CSSProperties = {
  padding: '8px 14px',
  borderRadius: 8,
  border: '1px solid #d1d5db',
  background: '#fff',
  color: '#374151',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

const confirmBtnStyle: React.CSSProperties = {
  padding: '8px 14px',
  borderRadius: 8,
  border: '1px solid #4338ca',
  background: 'linear-gradient(135deg, #6366f1, #4338ca)',
  color: '#fff',
  fontSize: 13,
  fontWeight: 700,
  cursor: 'pointer',
};

const confirmBtnDisabledStyle: React.CSSProperties = {
  ...confirmBtnStyle,
  background: '#c7d2fe',
  borderColor: '#c7d2fe',
  color: '#1e1b4b',
  cursor: 'not-allowed',
};

const confirmBtnWarnStyle: React.CSSProperties = {
  ...confirmBtnStyle,
  background: 'linear-gradient(135deg, #f97316, #c2410c)',
  border: '1px solid #9a3412',
};

const residencyLoadingStyle: React.CSSProperties = {
  padding: '8px 12px',
  background: '#f9fafb',
  border: '1px dashed #e5e7eb',
  borderRadius: 8,
  fontSize: 12,
  color: '#6b7280',
  marginBottom: 12,
};

const residencyOkStyle: React.CSSProperties = {
  padding: '8px 12px',
  background: '#f0fdf4',
  border: '1px solid #bbf7d0',
  borderRadius: 8,
  marginBottom: 12,
};

const residencyMismatchStyle: React.CSSProperties = {
  padding: '10px 12px',
  background: '#fef2f2',
  border: '1px solid #fecaca',
  borderRadius: 8,
  marginBottom: 12,
};

const residencyNeutralStyle: React.CSSProperties = {
  padding: '8px 12px',
  background: '#f8fafc',
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  marginBottom: 12,
};

const changeBtnStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 14px',
  borderRadius: 8,
  border: '1px solid #c2410c',
  background: 'linear-gradient(135deg, #f97316, #c2410c)',
  color: '#fff',
  fontSize: 13,
  fontWeight: 700,
  cursor: 'pointer',
  marginBottom: 4,
};

const chainBoxStyle: React.CSSProperties = {
  marginTop: 10,
  padding: '10px 12px',
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
};

const chainRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '4px 0',
  fontSize: 12,
};

const chainDetailStyle: React.CSSProperties = {
  marginLeft: 'auto',
  fontFamily: 'monospace',
  fontSize: 11,
  color: '#6b7280',
};

const chainErrorStyle: React.CSSProperties = {
  marginTop: 8,
  padding: '6px 8px',
  background: '#fef2f2',
  color: '#991b1b',
  borderRadius: 6,
  fontSize: 11,
};

const chainRetryBtnStyle: React.CSSProperties = {
  marginTop: 8,
  padding: '6px 12px',
  borderRadius: 6,
  border: '1px solid #d1d5db',
  background: '#fff',
  color: '#374151',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};

function chainStatusDotStyle(status: ChainStepStatus): React.CSSProperties {
  const m: Record<ChainStepStatus, React.CSSProperties> = {
    pending:     { background: '#f3f4f6', color: '#9ca3af', border: '1px solid #e5e7eb' },
    in_progress: { background: '#dbeafe', color: '#1d4ed8', border: '1px solid #93c5fd' },
    done:        { background: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0' },
    skipped:     { background: '#f3f4f6', color: '#6b7280', border: '1px dashed #d1d5db' },
    failed:      { background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' },
  };
  return {
    ...m[status],
    width: 18,
    height: 18,
    borderRadius: 999,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 11,
    fontWeight: 700,
    flexShrink: 0,
  };
}

function chainStatusGlyph(status: ChainStepStatus): string {
  switch (status) {
    case 'pending':     return '·';
    case 'in_progress': return '◌';
    case 'done':        return '✓';
    case 'skipped':     return '—';
    case 'failed':      return '!';
  }
}

function chainLabelStyle(status: ChainStepStatus): React.CSSProperties {
  return {
    fontWeight: status === 'in_progress' || status === 'failed' ? 600 : 500,
    color: status === 'done'    ? '#065f46'
         : status === 'failed'  ? '#991b1b'
         : status === 'skipped' ? '#6b7280'
         : '#1f2937',
  };
}

function residencyPillStyle(state: 'ok' | 'mismatch' | 'none' | 'skip'): React.CSSProperties {
  const m: Record<string, React.CSSProperties> = {
    ok:       { background: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0' },
    mismatch: { background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' },
    none:     { background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' },
    skip:     { background: '#e5e7eb', color: '#374151', border: '1px solid #d1d5db' },
  };
  return {
    ...m[state],
    padding: '1px 8px',
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  };
}
